import {
  getCurrentAdminApiKey,
  refreshAdminApiKey,
  refreshApiKey,
} from "../config";
import { runExclusiveAuthRefresh } from "./authRefreshGate";
import {
  ensureFreshToken,
  notifyAuthTokenRotated,
  waitForRotatedToken,
} from "./tokenRefreshManager";


export const maskSecret = (value) => {
  if (!value) {
    return "(empty)";
  }

  const str = String(value);
  if (str.length <= 8) {
    return "***";
  }

  return `${str.slice(0, 4)}...${str.slice(-4)}`;
};

export const getRequestLabel = (config) => {
  if (!config) {
    return "(unknown request)";
  }

  const method = String(config.method || "get").toUpperCase();
  const url = config.url || config.baseURL || "";
  return `${method} ${url}`;
};

const normalizeMessage = (message) =>
  String(message || "")
    .toLowerCase()
    .trim();

export const getAuthBoundaryMessage = (error) => {
  const message = error?.response?.data?.message;

  if (typeof message === "string") {
    return message;
  }

  if (message?.en) {
    return message.en;
  }

  if (message?.ar) {
    return message.ar;
  }

  return error?.response?.data?.data?.error || "";
};

export const isApiKeyExpiredError = (error) => {
  if (!error?.response) {
    return false;
  }

  const { status, data, headers } = error.response;
  const headerExpired =
    String(headers?.["x-api-key-expired"] || "").toLowerCase() === "true";

  // Only treat as a real API-key expiry when the backend signals it explicitly.
  // Do NOT match generic JWT rejection ("Unauthenticated") by message alone.
  if (status === 498 && data?.code === "api_key_expired") {
    return true;
  }

  if (headerExpired || data?.code === "api_key_expired") {
    return true;
  }

  const message = normalizeMessage(getAuthBoundaryMessage(error));
  return (
    message.includes("api key expired") ||
    message.includes("invalid api key format")
  );
};

export const hasExhaustedApiKeyRetry = (error) =>
  Boolean(error?.config?._apiKeyRetry);

export const shouldAttemptApiKeyRefresh = (error) =>
  isApiKeyExpiredError(error) && !hasExhaustedApiKeyRetry(error);

const isLogoutRequest = (config) =>
  String(config?.url || "").includes("/logout");

export const shouldSkipApiKeyRefresh = (error) =>
  isLogoutRequest(error?.config);

export const isInactivityTimeoutError = (error) =>
  normalizeMessage(getAuthBoundaryMessage(error)).includes("inactivity");

const LOGIN_REDIRECT_MESSAGE_FRAGMENTS = [
  "invalid or missing api key",
  "invalid api key format",
  "invalid api key",
  "api key expired",
  "session expired due to inactivity",
  "vpn or proxy detected",
  "unable to detect country from ip",
  "access denied: ip does not allow",
  "access denied: ip is restricted",
  "access denied: country is restricted",
  "forbidden. you do not have permission to access this resource",
  "two-factor authentication required",
  "2fa required",
  "too many requests. please try again later",
];

export const shouldRedirectToLoginForApiError = (error) => {
  const status = error?.response?.status;

  if (status === 401) {
    return true;
  }

  if (status !== 403 && status !== 429) {
    return false;
  }

  const normalizedMessage = normalizeMessage(getAuthBoundaryMessage(error));

  return LOGIN_REDIRECT_MESSAGE_FRAGMENTS.some((fragment) =>
    normalizedMessage.includes(fragment),
  );
};

// Resolves a fresh token to replay a stale-token 401 with.
const resolveFreshTokenForRetry = async (
  requestToken,
  instance,
  logAuthDebug,
) => {
  const storedToken = localStorage.getItem("authToken");

  if (storedToken && storedToken !== requestToken) {
    return storedToken;
  }

  logAuthDebug?.("401 with stale token - waiting for rotation or probing", {
    requestToken: maskSecret(requestToken),
  });

  return new Promise((resolve) => {
    let pending = 2;
    let resolved = false;

    const finishWith = (token, source) => {
      if (resolved) {
        return;
      }

      if (token && token !== requestToken) {
        resolved = true;
        logAuthDebug?.(`Fresh token obtained via ${source}`, {
          newToken: maskSecret(token),
        });
        resolve(token);
        return;
      }

      pending -= 1;
      if (pending > 0) {
        return;
      }

      resolved = true;
      const latest = localStorage.getItem("authToken");
      resolve(latest && latest !== requestToken ? latest : null);
    };

    waitForRotatedToken(requestToken).then((token) =>
      finishWith(token, "in-flight response"),
    );
    ensureFreshToken(instance, { force: true }).then((token) =>
      finishWith(token, "single-flight refresh"),
    );
  });
};

export const retryRequestWithLatestAuthToken = async (
  error,
  instance,
  logAuthDebug,
) => {
  const originalRequest = error.config;

  if (!originalRequest || originalRequest._authRetry) {
    return null;
  }

  const requestAuthHeader = String(
    originalRequest.headers?.Authorization || "",
  );
  const requestToken = requestAuthHeader.replace(/^Bearer\s+/i, "");

  const freshToken = await runExclusiveAuthRefresh(() =>
    resolveFreshTokenForRetry(requestToken, instance, logAuthDebug),
  );

  if (!freshToken) {
    return null;
  }

  originalRequest._authRetry = true;
  originalRequest.headers = originalRequest.headers || {};
  originalRequest.headers.Authorization = `Bearer ${freshToken}`;
  // Sibling token rotations also refresh the encrypted admin API key.
  originalRequest.headers["ADMIN-API-KEY"] = getCurrentAdminApiKey();

  logAuthDebug?.("Replaying request with fresh auth token", {
    request: getRequestLabel(originalRequest),
  });

  try {
    const retryResponse = await instance(originalRequest);
    logAuthDebug?.("Retry with fresh auth token succeeded - logout avoided", {
      request: getRequestLabel(originalRequest),
      status: retryResponse?.status,
    });
    return retryResponse;
  } catch (retryError) {
    logAuthDebug?.("Retry with fresh auth token failed", {
      request: getRequestLabel(originalRequest),
      status: retryError?.response?.status,
      message: retryError?.message,
    });
    return null;
  }
};

export const retryRequestWithFreshAdminApiKey = async (
  error,
  instance,
  logAuthDebug,
) => {
  const originalRequest = error.config;

  if (!originalRequest) {
    logAuthDebug?.("Admin API key refresh skipped: missing request config", {
      status: error?.response?.status,
      code: error?.response?.data?.code,
    });
    return null;
  }

  if (originalRequest._apiKeyRetry) {
    logAuthDebug?.("Admin API key refresh skipped: already retried once", {
      request: getRequestLabel(originalRequest),
      status: error?.response?.status,
      code: error?.response?.data?.code,
    });
    return null;
  }

  const requestApiKey = originalRequest.headers?.["ADMIN-API-KEY"];
  const previousApiKey = getCurrentAdminApiKey();

  logAuthDebug?.("Admin API key issue - pausing siblings and refreshing", {
    request: getRequestLabel(originalRequest),
    status: error?.response?.status,
    code: error?.response?.data?.code,
    previousApiKey: maskSecret(previousApiKey),
  });

  const newAdminApiKey = await runExclusiveAuthRefresh(() => {
    const currentApiKey = getCurrentAdminApiKey();
    if (currentApiKey && currentApiKey !== requestApiKey) {
      return currentApiKey;
    }
    return refreshAdminApiKey();
  });

  originalRequest._apiKeyRetry = true;
  originalRequest.headers = originalRequest.headers || {};
  originalRequest.headers["ADMIN-API-KEY"] = newAdminApiKey;
  const latestToken = localStorage.getItem("authToken");
  if (latestToken) {
    originalRequest.headers.Authorization = `Bearer ${latestToken}`;
  }

  logAuthDebug?.("Admin API key refreshed - retrying request", {
    request: getRequestLabel(originalRequest),
    newApiKey: maskSecret(newAdminApiKey),
  });

  try {
    const retryResponse = await instance(originalRequest);
    logAuthDebug?.("Admin API key refresh retry succeeded", {
      request: getRequestLabel(originalRequest),
      status: retryResponse?.status,
    });
    return retryResponse;
  } catch (retryError) {
    logAuthDebug?.("Admin API key refresh retry failed", {
      request: getRequestLabel(originalRequest),
      status: retryError?.response?.status,
      code: retryError?.response?.data?.code,
      message: retryError?.message,
    });
    throw retryError;
  }
};

export const applyRefreshedAuthTokenFromResponse = (
  response,
  logAuthDebug,
) => {
  const newToken = response?.headers?.["x-auth-token"];

  if (!newToken) {
    return response;
  }

  const previousToken = localStorage.getItem("authToken");
  const previousAdminApiKey = getCurrentAdminApiKey();

  logAuthDebug?.("New auth token received from response header", {
    request: getRequestLabel(response?.config),
    status: response?.status,
    previousToken: maskSecret(previousToken),
    newToken: maskSecret(newToken),
  });

  localStorage.setItem("authToken", newToken);
  localStorage.setItem("loginTime", String(Date.now()));

  // Wake any request currently waiting on a stale-token 401.
  notifyAuthTokenRotated(newToken);

  logAuthDebug?.("API keys refresh triggered after token update", {
    request: getRequestLabel(response?.config),
    previousAdminApiKey: maskSecret(previousAdminApiKey),
  });

  const newAdminApiKey = refreshAdminApiKey();
  refreshApiKey();

  logAuthDebug?.("Token refresh completed", {
    request: getRequestLabel(response?.config),
    tokenSaved: true,
    newToken: maskSecret(newToken),
    newAdminApiKey: maskSecret(newAdminApiKey),
  });

  return response;
};
